import React from 'react';
import clsx from 'clsx';
import { Colors, Radius, Spacing } from '@common/tokens';

type CardGroupProps = React.PropsWithChildren<{
  /**
   * `stacked` renders children as standalone cards with a gap between them.
   * `joined` renders a single bordered shell where children share borders via dividers
   * (child `Card`s are visually flattened — border, shadow and radius are stripped).
   */
  variant?: 'stacked' | 'joined';
  /** Controlled selection. Matches a child's `id` prop (or `key`); `null` means none. */
  selectedId?: string | null;
  /**
   * Selection callback — receives the toggled child's id, or `null` when the selected
   * child is clicked again (deselect). Omit to render a static, non-interactive group.
   */
  onSelect?: (id: string | null) => void;
  /** Keys of children that should not be selectable even when `onSelect` is provided. */
  disabledKeys?: string[];
  className?: string;
  'data-testid'?: string;
}>;

/**
 * Renders `Card`s (or any keyed children) as a vertical list and owns the selection
 * mechanics: toggle on click, Enter-key support and the selected ring. Selection state
 * lives with the consumer (`selectedId` / `onSelect`); what selection means is up to it.
 */
export const CardGroup = ({
  variant = 'stacked',
  selectedId = null,
  onSelect,
  disabledKeys,
  className,
  children,
  'data-testid': dataTestId,
}: CardGroupProps) => {
  const renderItem = (child: React.ReactNode) => {
    if (!React.isValidElement(child)) return child;

    const id = getChildId(child);
    if (onSelect && !id) {
      console.warn(
        'CardGroup: child without an `id` prop or `key` cannot be selected.'
      );
    }
    const isDisabled = Boolean(id && disabledKeys?.includes(id));
    const isInteractive = Boolean(onSelect && id && !isDisabled);
    const isSelected = isInteractive && selectedId === id;
    const toggleSelection = () => onSelect?.(isSelected ? null : id);

    const handleClick = (event: React.MouseEvent) => {
      if (isInnerInteractiveElement(event.target)) return;
      toggleSelection();
    };

    const handleKeyDown = (event: React.KeyboardEvent) =>
      activateItemOnKey(event, toggleSelection);

    return (
      <div
        className={clsx('wk-card-group__item', {
          'wk-card-group__item--interactive': isInteractive,
          'wk-card-group__item--selected': isSelected,
        })}
        role={isInteractive ? 'button' : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        onClick={isInteractive ? handleClick : undefined}
        onKeyDown={isInteractive ? handleKeyDown : undefined}
      >
        {child}
      </div>
    );
  };

  return (
    <div
      className={clsx('wk-card-group', `wk-card-group--${variant}`, className)}
      data-testid={dataTestId}
    >
      {React.Children.map(children, renderItem)}
      <style jsx>{`
        .wk-card-group {
          width: 100%;
        }
        .wk-card-group--stacked {
          display: flex;
          flex-direction: column;
          gap: ${Spacing.sm}px;
        }
        .wk-card-group--joined {
          background: ${Colors.white};
          border: 1px solid ${Colors.steelLighter};
          border-radius: ${Radius.sm};
          overflow: hidden;
        }
        /*
         * Item wrappers are rendered through a callback, so styled-jsx cannot attach its
         * scoping class to them — pierce with :global() anchored under the scoped root.
         */
        .wk-card-group :global(.wk-card-group__item) {
          width: 100%;
        }
        .wk-card-group :global(.wk-card-group__item--interactive) {
          cursor: pointer;
          outline: none;
          transition: box-shadow 0.12s ease;
        }
        .wk-card-group--stacked :global(.wk-card-group__item) {
          border-radius: ${Radius.sm};
        }
        .wk-card-group--stacked :global(.wk-card-group__item--selected) {
          box-shadow: 0 0 0 2px ${Colors.elm};
        }
        .wk-card-group--joined :global(.wk-card-group__item) {
          border-bottom: 1px solid ${Colors.steelLighter};
        }
        .wk-card-group--joined :global(.wk-card-group__item:first-child) {
          border-top-left-radius: ${Radius.sm};
          border-top-right-radius: ${Radius.sm};
        }
        .wk-card-group--joined :global(.wk-card-group__item:last-child) {
          border-bottom: none;
          border-bottom-left-radius: ${Radius.sm};
          border-bottom-right-radius: ${Radius.sm};
        }
        .wk-card-group--joined
          :global(.wk-card-group__item--interactive:hover) {
          background: ${Colors.steelLightest};
        }
        /*
         * The ring is drawn by an ::after overlay so it paints above child backgrounds
         * (e.g. the Card header) — an inset shadow on the item itself would be covered
         * by them. Inset so the shell's overflow: hidden never clips it; border-radius
         * is inherited from the item (rounded on first/last corners, square in the
         * middle).
         */
        .wk-card-group--joined :global(.wk-card-group__item--selected) {
          position: relative;
        }
        .wk-card-group--joined :global(.wk-card-group__item--selected::after) {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          border-radius: inherit;
          box-shadow: inset 0 0 0 2px ${Colors.elm};
          pointer-events: none;
        }
        /* Joined groups flatten child Cards so rows share borders via the dividers above. */
        .wk-card-group--joined :global(.wk-card-group__item > .wk-card) {
          border: none;
          box-shadow: none;
          border-radius: 0;
          background: transparent;
        }
      `}</style>
    </div>
  );
};

/**
 * Reads the identity used for selection from a child: an explicit `id` prop wins,
 * falling back to the React `key`.
 */
function getChildId(child: React.ReactElement): string | null {
  const propId = (child.props as { id?: unknown }).id;

  if (typeof propId === 'string') return propId;

  if (child.key !== null && child.key !== undefined) {
    return String(child.key).replace(/^\.\$/, '');
  }

  return null;
}

/** Ignores clicks bubbling from interactive elements inside a card. */
function isInnerInteractiveElement(target: EventTarget): boolean {
  return target instanceof HTMLElement && Boolean(target.closest('button, a'));
}

/**
 * Activates a `role="button"` item on Enter or Space per the WAI-ARIA button
 * pattern, invoking `toggle` unless the key originated on an inner interactive
 * element. `preventDefault` stops Space from scrolling the page.
 */
function activateItemOnKey(
  event: React.KeyboardEvent,
  toggle: () => void
): void {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  if (isInnerInteractiveElement(event.target)) return;
  event.preventDefault();
  toggle();
}
